import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import {
  Drawer, List, ListItem, ListItemIcon, ListItemText,
  Box, Typography, Tooltip, Divider, IconButton
} from '@mui/material';
import {
  Dashboard, People, DriveEta, AttachMoney, Feedback,
  PersonAdd, LocalTaxi, Map, CardMembership, CalendarToday,
  ChevronLeft, ChevronRight, DirectionsCar, Payment, TrendingUp, Article, Insights, Campaign
} from '@mui/icons-material';

const ACCENT = '#55AA22';
const SIDEBAR_BG = 'linear-gradient(180deg, #0a2e0a 0%, #116611 60%, #338800 100%)';

const Sidebar = ({ open, onToggle, width, collapsedWidth }) => {
  const { t } = useTranslation();
  const location = useLocation();

  const menuSections = [
    {
      label: 'Overview',
      items: [
        { text: t('dashboard', 'Dashboard'), icon: <Dashboard />, path: '/dashboard' },
        { text: t('businessAnalytics', 'Business Analytics'), icon: <Insights />, path: '/analytics' },
        { text: t('bookings', 'Bookings'), icon: <CalendarToday />, path: '/bookings' },
        { text: t('liveMap', 'Live Map'), icon: <Map />, path: '/live-map' },
        { text: t('customers', 'Customers'), icon: <People />, path: '/customers' },
        { text: 'Push Campaigns', icon: <Campaign />, path: '/push-campaigns' },
      ],
    },
    {
      label: 'Approvals',
      items: [
        { text: t('ownerApproval', 'Owner Approvals'), icon: <PersonAdd />, path: '/owner-approval' },
        { text: t('driverApproval', 'Driver Approvals'), icon: <LocalTaxi />, path: '/driver-approval' },
        { text: t('vehicleApproval', 'Vehicle Approvals'), icon: <DriveEta />, path: '/vehicle-approval' },
        { text: t('pendingMemberships', 'Pending Memberships'), icon: <CardMembership />, path: '/pending-memberships' },
      ],
    },
    {
      label: 'Management',
      items: [
        { text: t('drivers', 'Drivers'), icon: <People />, path: '/driver-list' },
        { text: t('owners', 'Owners'), icon: <People />, path: '/owner-list' },
        { text: t('vehicles', 'Vehicles'), icon: <DirectionsCar />, path: '/vehicle-list' },
        { text: t('membership', 'Membership Management'), icon: <CardMembership />, path: '/membership-management' },
        { text: t('zoneManagement', 'Zone Management'), icon: <Map />, path: '/zone-management' },
        { text: t('rateManagement', 'Rate Management'), icon: <AttachMoney />, path: '/rate-management' },
        { text: t('zoneDriverPricing', 'Zone Driver Pricing'), icon: <TrendingUp />, path: '/zone-driver-pricing' },
      ],
    },
    {
      label: 'Settings',
      items: [
        { text: t('razorpayPayments', 'Razorpay Payments'), icon: <Payment />, path: '/razorpay-settings' },
        { text: t('customerCms', 'Customer CMS'), icon: <Article />, path: '/customer-cms' },
        { text: t('driverCms', 'Driver CMS'), icon: <Article />, path: '/driver-cms' },
        { text: t('feedback', 'Customer Feedback'), icon: <Feedback />, path: '/feedback' },
      ],
    },
  ];

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', background: SIDEBAR_BG, overflow: 'hidden' }}>
      {/* Brand Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'space-between' : 'center',
          px: open ? 2.5 : 1,
          py: 2,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          minHeight: 72,
        }}
      >
        {open && (
          <Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.3rem',
                background: `linear-gradient(135deg, #ffffff 0%, #99CC77 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.5px',
                lineHeight: 1.1,
              }}
            >
              Drain Go 'Udaya Groups Pvt. Ltd.'
            </Typography>
            <Typography sx={{ color: 'rgba(153, 204, 119, 0.6)', fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Admin Panel
            </Typography>
          </Box>
        )}
        <IconButton
          onClick={onToggle}
          size="small"
          sx={{
            color: 'rgba(255,255,255,0.55)',
            bgcolor: 'rgba(250, 242, 242, 0.07)',
            '&:hover': { bgcolor: 'rgba(85,170,34,0.25)', color: ACCENT },
            borderRadius: '8px',
            width: 32,
            height: 32,
          }}
        >
          {open ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
        </IconButton>
      </Box>

      {/* Menu List */}
      <Box sx={{ overflowY: 'auto', overflowX: 'hidden', flexGrow: 1, py: 1 }}>
        {menuSections.map((section, sIdx) => (
          <Box key={sIdx}>
            {open && (
              <Typography
                sx={{
                  px: 2.5,
                  pt: sIdx === 0 ? 1.5 : 1,
                  pb: 0.5,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: 'rgba(153, 204, 119, 0.45)',
                }}
              >
                {section.label}
              </Typography>
            )}
            <List disablePadding>
              {section.items.map((item, idx) => {
                const isActive = location.pathname === item.path;
                const listItemContent = (
                  <ListItem
                    button
                    component={Link}
                    to={item.path}
                    key={idx}
                    sx={{
                      mx: open ? 1.5 : 1,
                      mb: 0.5,
                      borderRadius: '10px',
                      px: open ? 1.5 : 1,
                      py: 1,
                      width: open ? 'auto' : 'fit-content',
                      justifyContent: open ? 'flex-start' : 'center',
                      color: isActive ? '#ffffff' : 'rgba(255,255,255,0.65)',
                      background: isActive
                        ? `linear-gradient(90deg, rgba(85,170,34,0.25) 0%, rgba(85,170,34,0.05) 100%)`
                        : 'transparent',
                      borderLeft: isActive ? `3px solid ${ACCENT}` : '3px solid transparent',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'rgba(255,255,255,0.07)',
                        color: '#ffffff',
                        borderLeft: `3px solid rgba(85,170,34,0.5)`,
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: open ? 36 : 0,
                        color: isActive ? ACCENT : 'rgba(255,255,255,0.55)',
                        '& svg': { fontSize: '1.25rem' },
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {open && (
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: isActive ? 700 : 500,
                        }}
                      />
                    )}
                  </ListItem>
                );

                return open ? (
                  listItemContent
                ) : (
                  <Tooltip title={item.text} placement="right" key={idx}>
                    {listItemContent}
                  </Tooltip>
                );
              })}
            </List>
            {sIdx < menuSections.length - 1 && (
              <Divider sx={{ mx: 2, my: 0.5, borderColor: 'rgba(255,255,255,0.07)' }} />
            )}
          </Box>
        ))}
      </Box>

      {/* Footer */}
      <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.08)', px: 2.5, py: 2 }}>
        {open ? (
          <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.7rem', textAlign: 'center' }}>
            Drain Go 'Udaya Groups Pvt. Ltd.' © {new Date().getFullYear()}
          </Typography>
        ) : (
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ACCENT, mx: 'auto', boxShadow: `0 0 8px ${ACCENT}` }} />
        )}
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: open ? width : collapsedWidth,
        flexShrink: 0,
        position: 'fixed',
        '& .MuiDrawer-paper': {
          width: open ? width : collapsedWidth,
          boxSizing: 'border-box',
          border: 'none',
          boxShadow: '4px 0 24px rgba(0,0,0,0.15)',
          transition: 'width 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
          overflowX: 'hidden',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
